import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { MessageListener } from './message-listener';
export { MessageListener };
import { messageListenerSchema } from '@schemas/business-objects/message-listener';
import { TenantFormGroup } from './tenant-form-group';
import { TokenFormGroup } from './token-form-group';
import type { ConditionWatcher } from './condition-watcher';
import type { PendingStateUpdate } from './pending-state-update';
import type { Timer } from './timer';

export class MessageListenerFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    boundaryEventId: SchemaFormControl;
    messageName: SchemaFormControl;
    interrupting: SchemaFormControl;
    status: SchemaFormControl;
    completedAt: SchemaFormControl;
    messageData: SchemaFormControl;
    createdAt: SchemaFormControl;
    updatedAt: SchemaFormControl;
    tokenId: SchemaFormControl;
    token: TokenFormGroup;
    tenantId: SchemaFormControl;
    tenant: TenantFormGroup;
  };

  declare value: Partial<MessageListener>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(messageListenerSchema, MessageListener, data, options);
  }

  get object(): MessageListener {
    return new MessageListener(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'token':
        return new TokenFormGroup();
      case 'tenant':
        return new TenantFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<MessageListener[]> {
    return MessageListener.find(filter);
  }

  static async findOne(filter?: any): Promise<MessageListener | null> {
    return MessageListener.findOne(filter);
  }

  static async findById(id: string, filter?: any): Promise<MessageListener> {
    return MessageListener.findById(id, filter);
  }

  static async create(
    data: Partial<MessageListener>,
  ): Promise<MessageListener> {
    return MessageListener.create(data as any);
  }

  static async updateById(
    id: string,
    updates: Partial<MessageListener>,
  ): Promise<MessageListener> {
    return MessageListener.updateById(id, updates as any);
  }

  static async deleteById(id: string): Promise<boolean> {
    return MessageListener.deleteById(id);
  }

  async trigger(options: any): Promise<any> {
    const instance = new MessageListener(this.getRawValue());
    return instance.trigger(options);
  }
}

export class MessageListenerFormArray extends PersistedFormArray {
  declare controls: MessageListenerFormGroup[];

  readonly entityName = 'MessageListener' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(messageListenerSchema, MessageListener, options);
  }

  protected override createItemGroup() {
    return new MessageListenerFormGroup();
  }

  get array(): MessageListener[] {
    return this.controls.map((group: MessageListenerFormGroup) => group.object);
  }
}

export class MessageListenerPersistedArray extends PersistedArray<MessageListener> {
  readonly entityName = 'MessageListener' as const;

  constructor(options?: PersistedArrayOptions) {
    super(MessageListener, options);
  }
}
